import { createFeatureSelector, createSelector } from "@ngrx/store";

import {
    ShopsStateData,
    selectHasShops,
    selectShops,
    selectShopsListHasError,
    selectShopsListLoading,
    selectShopsListLoaded,
} from "@hermes/states/shop-locator";

import * as fromProductLocator from "./product-locator.reducer";
import { ProductLocatorState, ShopStockRecord } from "./product-locator.state";

export const selectProductLocatorState =
    createFeatureSelector<ProductLocatorState>(
        fromProductLocator.PRODUCT_LOCATOR_FEATURE_KEY,
    );

export const selectShopsStock = createSelector(
    selectProductLocatorState,
    (productLocatorState: ProductLocatorState) =>
        productLocatorState.shopsStock,
);

export const selectShopsStockError = createSelector(
    selectProductLocatorState,
    (productLocatorState: ProductLocatorState) => productLocatorState.error,
);

export const selectShopsStockHasError = createSelector(
    selectProductLocatorState,
    (productLocatorState: ProductLocatorState) =>
        !!(
            productLocatorState?.error && productLocatorState.status === "error"
        ),
);

export const selectShopsStockLoading = createSelector(
    selectProductLocatorState,
    (productLocatorState: ProductLocatorState) =>
        productLocatorState.status === "loading",
);

export const selectShopsStockLoaded = createSelector(
    selectProductLocatorState,
    (productLocatorState: ProductLocatorState) =>
        productLocatorState.status === "loaded",
);

export const selectHasStockStock = createSelector(
    selectProductLocatorState,
    (productLocatorState: ProductLocatorState) =>
        productLocatorState.shopsStock &&
        Object.keys(productLocatorState.shopsStock).length > 0,
);

export const selectProductLocatorLoading = createSelector(
    selectShopsStockLoading,
    selectShopsListLoading,
    (shopsStockLoading: boolean, shopsListLoading: boolean) =>
        shopsStockLoading || shopsListLoading,
);

export const selectHasError = createSelector(
    selectShopsStockHasError,
    selectShopsListHasError,
    selectProductLocatorLoading,
    (
        shopsStockError: boolean,
        shopsListError: boolean,
        productLocatorLoading: boolean,
    ) => !productLocatorLoading && (shopsStockError || shopsListError),
);

export const selectHasList = createSelector(
    selectHasStockStock,
    selectHasShops,
    (hasStock: boolean, hasStores: boolean) => hasStock && hasStores,
);

export const selectShopIdsWithStock = createSelector(
    selectShops,
    selectShopsStock,
    (shops: ShopsStateData, shopsStock: ShopStockRecord) => {
        /**
         * Note : In ShopStockRecord shopId attribut is the real cegidId
         */
        const cegidIds = Object.keys(shopsStock);
        return Object.values(shops)
            .filter((shop) => cegidIds.includes(shop.cegidId))
            .map((shop) => shop.shopId);
    },
);

// select and match if all condition are correct for open PL tray
export const selectPLReadyToOpen = createSelector(
    selectShopsStockLoaded,
    selectShopsListLoaded,
    selectHasList,
    selectShopIdsWithStock,
    (
        shopsStockLoaded: boolean,
        shopsListLoaded: boolean,
        hasList: boolean,
        shopIdsWithStock: string[],
    ) =>
        shopsStockLoaded &&
        shopsListLoaded &&
        hasList &&
        shopIdsWithStock.length > 0,
);

export const selectProductHasNoShopsStock = createSelector(
    selectShopsStockLoaded,
    selectShopsListLoaded,
    selectHasList,
    selectShopIdsWithStock,
    (
        shopsStockLoaded: boolean,
        shopsListLoaded: boolean,
        hasList: boolean,
        shopIdsWithStock: string[],
    ) =>
        shopsStockLoaded &&
        shopsListLoaded &&
        (!hasList || shopIdsWithStock.length === 0),
);

export const selectShopIdsWithoutStock = createSelector(
    selectShops,
    selectShopsStock,
    (shops: ShopsStateData, shopsStock: ShopStockRecord) => {
        /**
         * Note : In ShopStockRecord, key is the cegidId
         */
        const cegidIds = Object.keys(shopsStock);

        // Based on the shops loaded in the ShopLocator feature,
        // we return the IDs of those that have no stock in the ProductLocator feature (= no entry)
        return Object.values(shops)
            .filter((shop) => !cegidIds.includes(shop.cegidId))
            .map((shop) => shop.shopId);
    },
);
